import type { MenuDataItem } from '@ant-design/pro-components';
import type { RouteAccess } from '@/access';
import { PERMISSION_CONFIG, hasModulePermission } from '@/config/permissionConfig';

type MenuDataItemWithRoutes = Omit<MenuDataItem, 'routes' | 'children'> & {
  children?: MenuDataItemWithRoutes[];
  routes?: MenuDataItemWithRoutes[];
};

/**
 * Production-ready menu filter with strict permission enforcement
 * This is the single source of truth for menu visibility
 */
export function filterMenuByPermissions(
  menuData: MenuDataItem[],
  access: RouteAccess,
  permissions: string[],
  _debug = false,
): MenuDataItem[] {
  // No permissions = empty menu (fail-safe)
  if (!permissions || permissions.length === 0) {
    return [];
  }

  // No menu data = empty menu
  if (!menuData || menuData.length === 0) {
    return [];
  }

  const filterMenuItem = (item: MenuDataItem): MenuDataItem | null => {
    const routes = (item as unknown as MenuDataItemWithRoutes).routes;
    // Preserve hidden items (they won't show in menu anyway)
    if (item.hideInMenu) {
      return item;
    }

    // Check access requirement
    if (item.access) {
      const accessValue = access[item.access];

      if (typeof accessValue === 'boolean') {
        if (!accessValue) {
          return null; // User doesn't have this specific permission flag
        }
      } else if (item.access.endsWith('Module')) {
        const moduleKey = item.access as keyof typeof PERMISSION_CONFIG.modulePermissionPrefixes;
        if (!hasModulePermission(moduleKey, permissions)) {
          return null; // Fallback for module checks when access flag is missing
        }
      } else {
        return null; // Unknown access key - fail closed
      }
    }

    // Process children recursively
    if (item.children && item.children.length > 0) {
      const filteredChildren = item.children
        .map((child) => filterMenuItem(child))
        .filter(Boolean) as MenuDataItem[];

      // Remove parent if no children are accessible and parent has no direct route
      if (filteredChildren.length === 0 && !item.component && !item.redirect) {
        return null;
      }

      return {
        ...item,
        children: filteredChildren,
      };
    }

    // Process routes recursively
    if (routes && routes.length > 0) {
      const filteredRoutes = routes
        .map((route) => filterMenuItem(route as unknown as MenuDataItem))
        .filter(Boolean) as MenuDataItem[];

      if (filteredRoutes.length === 0) {
        return null;
      }

      // Remove redirect-only entries that no longer have valid targets
      const validRoutes = filteredRoutes.filter((route) => {
        if (!route.redirect) {
          return true;
        }

        const targetPath = route.redirect;
        return filteredRoutes.some((candidate) => candidate.path === targetPath);
      });

      // If after removing invalid redirects we have no accessible child routes, drop the parent
      const hasAccessibleChild = validRoutes.some((route) => {
        if (route.redirect) {
          return false;
        }

        const hasComponent = Boolean(route.component);
        const hasChildren = Boolean(route.children && route.children.length > 0);
        const nestedRoutes = (route as unknown as MenuDataItemWithRoutes).routes;
        const hasNestedRoutes = Boolean(nestedRoutes && nestedRoutes.length > 0);

        return hasComponent || hasChildren || hasNestedRoutes;
      });

      if (!hasAccessibleChild) {
        return null;
      }

      return {
        ...item,
        routes: validRoutes,
      } as unknown as MenuDataItem;
    }

    return item;
  };

  // Filter all top-level menu items
  return menuData.map((item) => filterMenuItem(item)).filter(Boolean) as MenuDataItem[];
}

export function validateAccessObject(
  access: RouteAccess,
  permissions: string[],
  logWarnings = false,
): boolean {
  const isValid =
    Boolean(access) &&
    Array.isArray(permissions) &&
    Object.values(access).every((value) => typeof value === 'boolean');

  if (!isValid && logWarnings) {
    console.warn('[MenuPermissionFilter] Invalid access object or permissions list');
  }

  return isValid;
}
